import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AtajoCausal, Causal, CausalUso, Consecuencia, SolicitudLiberacion } from '../../models/liberacion.models';
import { DETALLE_MAX, DETALLE_MIN, validarDetalle } from '../../liberacion.utils';

/** Respaldo cuando no llega el uso real (falla la consulta o hay pocas causales): las mas usadas en PANTALLA LIBERA OP SAP al 2026-10. Si el codigo no existe en `motivos`, no se muestra. */
const ATAJOS = ['X5', 'X4', 'Z4'];
const ATAJOS_MIN = 3;
const ATAJOS_MAX = 4;

export interface FilaRevision {
  k: string;
  v: string;
}

/**
 * Causal (select + atajos sincronizados), detalle y paso de revision.
 * El contenido proyectado (entrega o pedidos del caso, "que pasara") se oculta durante la revision.
 */
@Component({
  selector: 'app-formulario-liberacion',
  templateUrl: './formulario-liberacion.component.html'
})
export class FormularioLiberacionComponent implements OnChanges {
  @Input() motivos: Causal[] = [];
  /** Causales mas usadas de los ultimos `diasTop` dias en `pais`; vacio si no hay datos. */
  @Input() topCausales: CausalUso[] = [];
  @Input() diasTop = 30;
  @Input() pais = '';
  /** Sin catalogo todavia (nada guardado): se muestra "Cargando causales...". */
  @Input() cargandoCausales = false;
  @Input() errorCausales = false;
  /** Filas fijas de la revision (OP/caso, pedido); se agregan causal y detalle. */
  @Input() filasRevision: FilaRevision[] = [];
  @Input() consecuencias: Consecuencia[] | null = null;
  /** false si falta elegir la entrega: se avisa al padre y no se avanza. */
  @Input() seleccionValida = true;
  @Input() enviando = false;
  @Output() confirmar = new EventEmitter<SolicitudLiberacion>();
  @Output() seleccionFaltante = new EventEmitter<void>();

  @ViewChild('causalSelect') causalSelect?: ElementRef<HTMLSelectElement>;
  @ViewChild('detalleArea') detalleArea?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('botonConfirmar') botonConfirmar?: ElementRef<HTMLButtonElement>;

  readonly detalleMax = DETALLE_MAX;
  readonly detalleMin = DETALLE_MIN;

  form = new FormGroup({
    causal: new FormControl('', Validators.required),
    detalle: new FormControl('')
  });

  enRevision = false;
  intentado = false;

  atajos: AtajoCausal[] = [];
  ayudaAbierta = false;

  get hayUso(): boolean {
    return this.atajos.some(a => a.total !== null);
  }

  ngOnChanges(): void {
    if (this.atajos.length && this.hayInteraccion) {
      // El usuario ya esta trabajando: no se reordenan los chips bajo su mouse; lo nuevo se ve en la proxima visita.
      this.atajos = this.atajos.filter(a => this.motivos.some(m => m.COD_CAUSAL === a.COD_CAUSAL));
      return;
    }
    this.atajos = this.armarAtajos();
  }

  private get hayInteraccion(): boolean {
    return this.form.dirty || !!this.form.value.causal;
  }

  /** Mas usadas primero (descripcion oficial del catalogo); si faltan para llegar al minimo, se completan con el respaldo sin repetir. */
  private armarAtajos(): AtajoCausal[] {
    const lista: AtajoCausal[] = [];
    for (const t of this.topCausales) {
      const cat = this.motivos.find(m => m.COD_CAUSAL === t.COD_CAUSAL);
      if (cat && lista.length < ATAJOS_MAX) {
        lista.push({ COD_CAUSAL: cat.COD_CAUSAL, DES: cat.DES, total: t.TOTAL });
      }
    }
    for (const cod of ATAJOS) {
      const cat = this.motivos.find(m => m.COD_CAUSAL === cod);
      if (cat && lista.length < ATAJOS_MIN && !lista.some(a => a.COD_CAUSAL === cod)) {
        lista.push({ COD_CAUSAL: cat.COD_CAUSAL, DES: cat.DES, total: null });
      }
    }
    return lista;
  }

  trackByCodigo(_: number, a: Causal): string {
    return a.COD_CAUSAL;
  }

  get causal(): Causal | null {
    return this.motivos.find(m => m.COD_CAUSAL === this.form.value.causal) || null;
  }

  get errorCausal(): string | null {
    return this.intentado && !this.causal ? 'Elige una causal.' : null;
  }

  get errorDetalle(): string | null {
    return this.intentado ? validarDetalle(this.form.value.detalle) : null;
  }

  get filas(): FilaRevision[] {
    return [
      ...this.filasRevision,
      { k: 'Causal', v: this.causal ? this.causal.DES : '' },
      { k: 'Detalle', v: (this.form.value.detalle || '').trim() }
    ];
  }

  elegirAtajo(a: Causal): void {
    this.form.patchValue({ causal: a.COD_CAUSAL });
  }

  enviar(): void {
    if (this.enRevision) {
      if (this.causal && !this.enviando) {
        this.confirmar.emit({ causal: this.causal, detalle: (this.form.value.detalle || '').trim() });
      }
      return;
    }

    this.intentado = true;
    if (!this.seleccionValida) {
      this.seleccionFaltante.emit();
    } else if (!this.causal) {
      this.enfocar(this.causalSelect);
    } else if (validarDetalle(this.form.value.detalle)) {
      this.enfocar(this.detalleArea);
    } else {
      this.enRevision = true;
      setTimeout(() => this.botonConfirmar && this.botonConfirmar.nativeElement.focus({ preventScroll: true }));
    }
  }

  private enfocar(el?: ElementRef<HTMLElement>): void {
    if (el) {
      el.nativeElement.focus({ preventScroll: true });
      el.nativeElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }
}
